/**
 * Реестр пилотов: два таба — «Развёрнутые» (CMDB из SD) и «Воронка» (из BC).
 *
 * - Развёрнутые: текущие инсталляции с support_override='pilot' или
 *   org.project_status='pilot'. Действия extend/convert/cancel/reopen.
 * - Воронка: read-only список из BC.Pilot (planned/active/completed/declined/frozen).
 *   Кнопка «Принять в работу» на каждой строке → создаёт Installation в SD
 *   через POST /api/v1/pilots/pipeline/migrate.
 */
import { Link, Navigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowRight, ArrowRightCircle, CalendarClock, Check,
  Clock, ExternalLink, Filter, GitMerge, History, Link2Off,
  MessageSquare, Pause, Pencil, RefreshCw, Rocket, RotateCcw, Workflow, X,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { fmtTime } from '../lib/format';
import { OrgCombobox } from '../components/OrgCombobox';
import {
  OUTCOME_COLOR, OUTCOME_LABEL, PHASE_COLOR, PHASE_LABEL,
  PIPELINE_STATUS_COLOR, PIPELINE_STATUS_LABEL,
  pilotsApi,
  type PilotPhase, type PilotRow, type PilotSummary,
  type PipelineRow, type PipelineStatus,
} from '../api/pilots';

type Tab = 'deployed' | 'pipeline';

export default function PilotsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>(() =>
    (localStorage.getItem('sd:pilots:tab') as Tab) || 'deployed');

  const switchTab = (t: Tab) => {
    setTab(t);
    localStorage.setItem('sd:pilots:tab', t);
  };

  if (!user) return null;
  if (user.type !== 'staff') return <Navigate to="/" replace />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Rocket className="text-blue-400" size={20} />
        <h1 className="text-2xl font-bold text-white">Пилоты</h1>
        <span className="text-xs text-slate-500">реестр SD ↔ воронка BC</span>
      </div>

      <div className="flex gap-1 border-b border-slate-800">
        <TabBtn active={tab === 'deployed'} onClick={() => switchTab('deployed')}
                icon={Rocket} label="Развёрнутые" />
        <TabBtn active={tab === 'pipeline'} onClick={() => switchTab('pipeline')}
                icon={Workflow} label="Воронка (из BC)" />
      </div>

      {tab === 'deployed' ? <DeployedTab /> : <PipelineTab />}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label }: {
  active: boolean; onClick: () => void; icon: any; label: string;
}) {
  return (
    <button onClick={onClick}
            className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px ${
              active ? 'border-blue-500 text-white'
                     : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}>
      <Icon size={14} /> {label}
    </button>
  );
}

// ── Tab «Развёрнутые» — прежний реестр ───────────────────────────────────────
function DeployedTab() {
  const [rows, setRows] = useState<PilotRow[]>([]);
  const [summary, setSummary] = useState<PilotSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [includeFinished, setIncludeFinished] = useState(false);
  const [phase, setPhase] = useState<PilotPhase | ''>('');
  const [expiringWithin, setExpiringWithin] = useState<number | ''>('');
  const [activeAction, setActiveAction] = useState<{
    kind: 'extend' | 'convert' | 'cancel' | 'reopen' | 'edit'; row: PilotRow;
  } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (includeFinished) params.include_finished = true;
      if (phase) params.phase = phase;
      if (expiringWithin !== '') params.expiring_within_days = expiringWithin;
      const [list, s] = await Promise.all([
        pilotsApi.list(params), pilotsApi.summary(),
      ]);
      setRows(list); setSummary(s);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ },
           [includeFinished, phase, expiringWithin]);

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500">
        Реальные инсталляции в режиме «Пилот» (`support_override='pilot'` или
        проектный статус организации — «Пилот»). Алерт-крон: 09:15 МСК (7/3/1/0).
      </div>
      {summary && <PilotSummaryCards summary={summary} />}

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-3 flex flex-wrap gap-2 items-center">
        <Filter size={14} className="text-slate-500" />
        <span className="text-xs text-slate-400">Фильтры:</span>
        <select value={phase}
                onChange={e => setPhase((e.target.value as PilotPhase) || '')}
                className="bg-slate-950 text-slate-200 border border-slate-700 rounded px-2 py-1 text-xs">
          <option value="">Все фазы</option>
          <option value="poc">PoC</option>
          <option value="trial">Trial</option>
          <option value="decision">Решение</option>
        </select>
        <select value={expiringWithin === '' ? '' : String(expiringWithin)}
                onChange={e => setExpiringWithin(e.target.value === '' ? '' : Number(e.target.value))}
                className="bg-slate-950 text-slate-200 border border-slate-700 rounded px-2 py-1 text-xs">
          <option value="">Без фильтра по сроку</option>
          <option value="0">Просрочка / истекает сегодня</option>
          <option value="3">≤ 3 дней</option>
          <option value="7">≤ 7 дней</option>
          <option value="14">≤ 14 дней</option>
          <option value="30">≤ 30 дней</option>
        </select>
        <label className="text-xs text-slate-300 inline-flex items-center gap-1 cursor-pointer">
          <input type="checkbox" checked={includeFinished}
                 onChange={e => setIncludeFinished(e.target.checked)} />
          Показать закрытые
        </label>
        <div className="ml-auto text-xs text-slate-500">
          Всего: <span className="text-slate-200">{rows.length}</span>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Загрузка…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            Развёрнутых пилотов нет. Чтобы зарегистрировать — откройте
            карточку организации, во вкладке «Инсталляции» поставьте
            «Пилот» и заполните дату окончания. Или перейдите в воронку
            и нажмите «Принять в работу» на нужной строке.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-950 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Организация</th>
                <th className="text-left px-3 py-2">Инсталляция</th>
                <th className="text-left px-3 py-2">Фаза</th>
                <th className="text-left px-3 py-2">Старт</th>
                <th className="text-left px-3 py-2">План до</th>
                <th className="text-right px-3 py-2">Осталось</th>
                <th className="text-right px-3 py-2">Прод.</th>
                <th className="text-right px-3 py-2">Тикеты</th>
                <th className="text-left px-3 py-2">Действия</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => <PilotTableRow key={r.installation_id} row={r} onAction={setActiveAction} />)}
            </tbody>
          </table>
        )}
      </div>

      {activeAction && activeAction.kind === 'edit' ? (
        <PilotEditModal
          row={activeAction.row}
          onClose={() => setActiveAction(null)}
          onDone={async () => { setActiveAction(null); await load(); }}
        />
      ) : activeAction ? (
        <PilotActionModal
          kind={activeAction.kind as 'extend' | 'convert' | 'cancel' | 'reopen'}
          row={activeAction.row}
          onClose={() => setActiveAction(null)}
          onDone={async () => { setActiveAction(null); await load(); }}
        />
      ) : null}
    </div>
  );
}

function PilotSummaryCards({ summary }: { summary: PilotSummary }) {
  const cards = [
    { label: 'Активных', value: summary.active, color: 'text-sky-300', icon: Rocket },
    { label: 'Истекают ≤7 дней', value: summary.expiring_7d, color: 'text-amber-300', icon: Clock },
    { label: 'Просрочены', value: summary.overdue, color: 'text-rose-300', icon: AlertTriangle },
    { label: 'Без даты конца', value: summary.no_end_date, color: 'text-slate-300', icon: CalendarClock },
    { label: 'Конвертированы', value: summary.converted, color: 'text-emerald-300', icon: Check },
    { label: 'Отменены', value: summary.cancelled, color: 'text-rose-400', icon: X },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      {cards.map(({ label, value, color, icon: Icon }) => (
        <div key={label} className="bg-slate-900 border border-slate-800 rounded-xl p-3">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Icon size={14} className={color} /> {label}
          </div>
          <div className={`text-2xl font-semibold mt-1 ${color}`}>{value}</div>
        </div>
      ))}
    </div>
  );
}

function PilotTableRow({
  row, onAction,
}: { row: PilotRow; onAction: (a: { kind: 'extend' | 'convert' | 'cancel' | 'reopen' | 'edit'; row: PilotRow }) => void }) {
  const daysLeftBadge = useMemo(() => {
    if (!row.is_active) return null;
    if (row.days_left === null) return <span className="text-slate-500 text-xs">—</span>;
    const dl = row.days_left;
    if (dl < 0) return <span className="text-rose-300 font-semibold">просрочка {-dl} дн</span>;
    if (dl === 0) return <span className="text-amber-300 font-semibold">сегодня</span>;
    if (dl <= 7) return <span className="text-amber-300">{dl} дн</span>;
    return <span className="text-slate-300">{dl} дн</span>;
  }, [row]);

  return (
    <tr className="border-t border-slate-800 hover:bg-slate-900/60">
      <td className="px-3 py-2">
        <Link to={`/organizations/${row.organization_id}`} className="text-blue-300 hover:underline">
          {row.organization_name}
        </Link>
        {row.organization_inn && (
          <div className="text-xs text-slate-500 font-mono">{row.organization_inn}</div>
        )}
      </td>
      <td className="px-3 py-2">
        <Link to={`/organizations/${row.organization_id}?tab=installations&inst=${row.installation_id}`}
              className="text-slate-200 hover:text-blue-300">
          {row.installation_name}
        </Link>
        <div className="text-xs text-slate-500">{row.product}</div>
      </td>
      <td className="px-3 py-2">
        {row.outcome ? (
          <span className={`px-2 py-0.5 rounded text-xs border ${OUTCOME_COLOR[row.outcome]}`}>
            {OUTCOME_LABEL[row.outcome]}
          </span>
        ) : row.phase ? (
          <span className={`px-2 py-0.5 rounded text-xs border ${PHASE_COLOR[row.phase]}`}>
            {PHASE_LABEL[row.phase]}
          </span>
        ) : <span className="text-slate-500 text-xs">—</span>}
      </td>
      <td className="px-3 py-2 text-xs text-slate-300">{row.started_at || '—'}</td>
      <td className="px-3 py-2 text-xs text-slate-300">{row.planned_end_at || '—'}</td>
      <td className="px-3 py-2 text-right">{daysLeftBadge}</td>
      <td className="px-3 py-2 text-right text-slate-400">{row.extended_count || ''}</td>
      <td className="px-3 py-2 text-right">
        <div className="text-xs text-slate-300">
          {row.tickets_open}<span className="text-slate-600">/</span>{row.tickets_total}
        </div>
        {row.last_ticket_at && (
          <div className="text-[10px] text-slate-500" title={row.last_ticket_at}>
            <MessageSquare size={9} className="inline mr-1" />{fmtTime(row.last_ticket_at)}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        {row.is_active ? (
          <div className="flex flex-wrap gap-1">
            <button onClick={() => onAction({ kind: 'edit', row })}
                    className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded text-xs inline-flex items-center gap-1"
                    title="Изменить даты, фазу, заметки, тип развёртывания">
              <Pencil size={11} /> Изменить
            </button>
            <button onClick={() => onAction({ kind: 'extend', row })}
                    className="bg-slate-800 hover:bg-slate-700 text-slate-200 px-2 py-1 rounded text-xs inline-flex items-center gap-1"
                    title="Продлить пилот">
              <History size={11} /> Продлить
            </button>
            <button onClick={() => onAction({ kind: 'convert', row })}
                    className="bg-emerald-900/60 hover:bg-emerald-800 text-emerald-200 px-2 py-1 rounded text-xs inline-flex items-center gap-1"
                    title="Перевести в платную поддержку">
              <ArrowRight size={11} /> В платный
            </button>
            <button onClick={() => onAction({ kind: 'cancel', row })}
                    className="bg-rose-900/60 hover:bg-rose-800 text-rose-200 px-2 py-1 rounded text-xs inline-flex items-center gap-1"
                    title="Закрыть пилот (отказ)">
              <Pause size={11} /> Закрыть
            </button>
            {row.bitrix_deal_url && (
              <a href={row.bitrix_deal_url} target="_blank" rel="noopener noreferrer"
                 className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded text-xs inline-flex items-center gap-1"
                 title="Открыть сделку в Bitrix24">
                <ExternalLink size={11} /> B24
              </a>
            )}
          </div>
        ) : (
          <button onClick={() => onAction({ kind: 'reopen', row })}
                  className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded text-xs inline-flex items-center gap-1"
                  title="Открыть пилот заново">
            <RotateCcw size={11} /> Открыть заново
          </button>
        )}
      </td>
    </tr>
  );
}

function PilotActionModal({
  kind, row, onClose, onDone,
}: {
  kind: 'extend' | 'convert' | 'cancel' | 'reopen';
  row: PilotRow; onClose: () => void; onDone: () => void;
}) {
  const [note, setNote] = useState('');
  const [days, setDays] = useState(30);
  const [newPhase, setNewPhase] = useState<PilotPhase | ''>('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (kind === 'extend') {
        await pilotsApi.extend(row.installation_id, {
          days, new_phase: newPhase || undefined, note: note || undefined,
        });
      } else if (kind === 'convert') {
        await pilotsApi.convert(row.installation_id, note || undefined);
      } else if (kind === 'cancel') {
        await pilotsApi.cancel(row.installation_id, note || undefined);
      } else {
        await pilotsApi.reopen(row.installation_id, note || undefined);
      }
      onDone();
    } finally { setBusy(false); }
  };

  const titles = {
    extend: `Продлить пилот «${row.installation_name}»`,
    convert: 'Конвертировать в платную поддержку',
    cancel: 'Закрыть пилот (отказ клиента)',
    reopen: 'Открыть пилот заново',
  };
  const descriptions = {
    extend: 'Plan-дата сдвинется вперёд, счётчик продлений увеличится на 1.',
    convert: 'support_override инсталляции станет «support», пилот закроется с outcome=converted.',
    cancel: 'support_override инсталляции станет «no_support», пилот закроется с outcome=cancelled.',
    reopen: 'Outcome сбросится в null, support_override снова станет «pilot».',
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-5">
        <h3 className="text-lg font-semibold text-white">{titles[kind]}</h3>
        <p className="text-xs text-slate-400 mt-1">
          {row.organization_name} → {row.installation_name}
        </p>
        <p className="text-xs text-slate-500 mt-2">{descriptions[kind]}</p>

        {kind === 'extend' && (
          <div className="space-y-2 mt-4">
            <label className="block text-xs text-slate-400">На сколько дней</label>
            <input type="number" value={days} min={1} max={365}
                   onChange={e => setDays(Number(e.target.value) || 1)}
                   className="w-full bg-slate-950 text-slate-200 border border-slate-700 rounded px-2 py-1 text-sm" />
            <label className="block text-xs text-slate-400">Новая фаза</label>
            <select value={newPhase}
                    onChange={e => setNewPhase((e.target.value as PilotPhase) || '')}
                    className="w-full bg-slate-950 text-slate-200 border border-slate-700 rounded px-2 py-1 text-sm">
              <option value="">Без изменения</option>
              <option value="poc">PoC</option>
              <option value="trial">Trial</option>
              <option value="decision">Решение</option>
            </select>
          </div>
        )}

        <div className="mt-3">
          <label className="block text-xs text-slate-400 mb-1">Заметка</label>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                    className="w-full bg-slate-950 text-slate-200 border border-slate-700 rounded px-2 py-1 text-sm"
                    placeholder="Будет добавлено в pilot_notes." />
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} disabled={busy}
                  className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded text-sm">
            Отмена
          </button>
          <button onClick={submit} disabled={busy}
                  className="bg-blue-700 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-sm">
            {busy ? 'Сохраняю…' : 'Подтвердить'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tab «Воронка» — read-only из BC + миграция ───────────────────────────────
function PipelineTab() {
  const [rows, setRows] = useState<PipelineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<PipelineStatus | ''>('');
  const [hideProcessed, setHideProcessed] = useState(false);
  const [migrating, setMigrating] = useState<PipelineRow | null>(null);

  const load = async (force = false) => {
    setLoading(true); setError(null);
    try {
      setRows(await pilotsApi.pipeline(status || undefined, force));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Ошибка загрузки');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { planned: 0, active: 0, completed: 0, declined: 0, frozen: 0 };
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  const visibleRows = useMemo(
    () => hideProcessed ? rows.filter(r => !r.sd_organization_id) : rows,
    [rows, hideProcessed],
  );
  const processedCount = rows.filter(r => r.sd_organization_id).length;

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500">
        Воронка пилотов из Battlecard (read-only, кэш 60с). Используется для
        планирования: запись становится «Развёрнутой» когда мы фактически
        развернули инсталляцию у клиента и нажали «Принять в работу».
      </div>

      <div className="grid grid-cols-5 gap-3">
        {(['planned', 'active', 'completed', 'declined', 'frozen'] as PipelineStatus[]).map(s => (
          <button key={s} onClick={() => setStatus(status === s ? '' : s)}
                  className={`bg-slate-900 border rounded-xl p-3 text-left ${
                    status === s ? 'border-blue-500' : 'border-slate-800 hover:border-slate-700'}`}>
            <div className="text-xs text-slate-500">{PIPELINE_STATUS_LABEL[s]}</div>
            <div className="text-2xl font-semibold text-white mt-1">{counts[s] || 0}</div>
          </button>
        ))}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-3 flex flex-wrap gap-3 items-center">
        <button onClick={() => load(true)} disabled={loading}
                title="Перечитать воронку из Battlecard (сбрасывает 60-сек кэш)"
                className="bg-slate-800 hover:bg-slate-700 disabled:bg-slate-900 text-slate-300 px-3 py-1.5 rounded text-xs inline-flex items-center gap-1">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Обновить
        </button>
        <label className="text-xs text-slate-300 inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={hideProcessed}
                 onChange={e => setHideProcessed(e.target.checked)} />
          Скрыть обработанные
          <span className="text-slate-500">
            (уже привязаны или взяты в работу — {processedCount})
          </span>
        </label>
        <div className="ml-auto text-xs text-slate-500">
          Показано: <span className="text-slate-200">{visibleRows.length}</span> из {rows.length}
        </div>
      </div>

      {error && (
        <div className="bg-rose-900/30 border border-rose-800 text-rose-200 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Загрузка из Battlecard…</div>
        ) : visibleRows.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            {rows.length === 0
              ? `Воронка пуста ${status ? `(фильтр: ${PIPELINE_STATUS_LABEL[status]})` : ''}.`
              : 'Все записи скрыты (обработаны). Снимите «Скрыть обработанные» чтобы увидеть.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-950 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Заказчик</th>
                <th className="text-left px-3 py-2">Статус</th>
                <th className="text-left px-3 py-2">Тип</th>
                <th className="text-left px-3 py-2">Старт → конец</th>
                <th className="text-right px-3 py-2">Сумма</th>
                <th className="text-left px-3 py-2">Ответственный</th>
                <th className="text-left px-3 py-2">В SD</th>
                <th className="text-left px-3 py-2">Действия</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(r => (
                <PipelineTableRow
                  key={r.bc_pilot_id} row={r}
                  onMigrate={setMigrating}
                  onUnlink={async (br) => {
                    const orgName = br.sd_organization_name || 'связанная организация SD';
                    if (!window.confirm(
                      `Отвязать BC.Pilot #${br.bc_pilot_id} (${br.customer_org || br.customer_name}) от «${orgName}»?\n\n` +
                      `Будет удалена Installation, созданная миграцией, и (если возможно) сама организация — но только если она была создана через миграцию и в ней нет тикетов/клиентов/других инсталляций.`
                    )) return;
                    try {
                      const res = await pilotsApi.unlink(br.bc_pilot_id);
                      const summary: string[] = [];
                      if (res.removed_link) summary.push('Удалена ручная связь');
                      if (res.removed_installation_id) summary.push('Удалена Installation');
                      if (res.removed_organization_id) summary.push('Удалена пустая Organization');
                      if (res.kept_organization_id) summary.push('Organization оставлена');
                      const all = summary.concat(res.notes);
                      if (all.length === 0) all.push('Связь не найдена — нечего откатывать.');
                      alert('Отвязка завершена:\n• ' + all.join('\n• '));
                    } catch (e: any) {
                      alert('Не удалось отвязать: ' + (e?.response?.data?.detail || e?.message || 'ошибка'));
                    }
                    await load();
                  }}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {migrating && (
        <MigratePipelineModal row={migrating}
          onClose={() => setMigrating(null)}
          onDone={async () => { setMigrating(null); await load(); }} />
      )}
    </div>
  );
}

function PipelineTableRow({ row, onMigrate, onUnlink }: {
  row: PipelineRow;
  onMigrate: (r: PipelineRow) => void;
  onUnlink: (r: PipelineRow) => void;
}) {
  const fmtDateRange = () => {
    const s = row.start_date ? row.start_date.slice(0, 10) : '—';
    const e = row.expected_end_date ? row.expected_end_date.slice(0, 10) : '—';
    return `${s} → ${e}`;
  };
  // Уже обработан: либо принят в работу (есть Installation), либо привязан
  // вручную (есть Organization, но без Installation). В обоих случаях
  // приглушаем строку — это уже «не требует решения». Migrate-кнопки больше нет.
  const isProcessed = !!row.sd_organization_id;
  const muted = isProcessed ? 'opacity-50' : '';
  const textClass = isProcessed ? 'text-slate-400' : 'text-slate-200';
  return (
    <tr className={`border-t border-slate-800 hover:bg-slate-900/60 ${muted}`}>
      <td className="px-3 py-2">
        <div className={textClass}>{row.customer_org || row.customer_name}</div>
        {row.customer_inn && (
          <div className="text-xs text-slate-500 font-mono">{row.customer_inn}</div>
        )}
        {row.sd_organization_id && row.sd_organization_name && (
          <div className="mt-1 text-xs flex items-center gap-1 flex-wrap">
            <span className="text-slate-500">→ в SD:</span>
            <Link to={`/organizations/${row.sd_organization_id}`}
                  className="text-blue-300 hover:underline">
              {row.sd_organization_name}
            </Link>
            {row.auto_match_warning && (
              <span title={row.auto_match_warning}
                    className="text-amber-300 inline-flex items-center gap-0.5"
                    aria-label="возможно ошибочная связь">
                <AlertTriangle size={10} /> ?
              </span>
            )}
          </div>
        )}
        {row.customer_contact_email && (
          <div className="mt-0.5">
            <a href={`mailto:${row.customer_contact_email}`}
               className="text-xs text-blue-300 hover:underline">{row.customer_contact_email}</a>
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <span className={`px-2 py-0.5 rounded text-xs border ${PIPELINE_STATUS_COLOR[row.status]}`}>
          {PIPELINE_STATUS_LABEL[row.status]}
        </span>
      </td>
      <td className={`px-3 py-2 text-xs ${isProcessed ? 'text-slate-500' : 'text-slate-300'}`}>
        {row.deployment_type || '—'}
      </td>
      <td className={`px-3 py-2 text-xs font-mono ${isProcessed ? 'text-slate-500' : 'text-slate-300'}`}>
        {fmtDateRange()}
      </td>
      <td className={`px-3 py-2 text-right text-xs ${isProcessed ? 'text-slate-500' : 'text-slate-300'}`}>
        {row.amount ? row.amount.toLocaleString('ru') : '—'}
      </td>
      <td className="px-3 py-2 text-xs">
        {row.owner_name ? (
          <>
            <div className={textClass}>{row.owner_name}</div>
            {row.owner_email && <div className="text-slate-500">{row.owner_email}</div>}
          </>
        ) : <span className="text-slate-500">—</span>}
      </td>
      <td className="px-3 py-2 text-xs">
        {row.sd_installation_id ? (
          <Link to={`/organizations/${row.sd_organization_id}?tab=installations&inst=${row.sd_installation_id}`}
                className="text-emerald-300 hover:underline inline-flex items-center gap-1"
                title="Пилот развёрнут — есть Installation в SD">
            <Check size={11} /> в работе
          </Link>
        ) : row.sd_organization_id ? (
          <span className="text-sky-300 inline-flex items-center gap-1"
                title="Привязан к организации SD, но Installation ещё не создан">
            <ArrowRightCircle size={11} /> привязан
          </span>
        ) : <span className="text-slate-500">—</span>}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {row.b24_deal_url && (
            <a href={row.b24_deal_url} target="_blank" rel="noopener noreferrer"
               className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded text-xs inline-flex items-center gap-1">
              <ExternalLink size={11} /> B24
            </a>
          )}
          {row.sd_organization_id && (
            <button onClick={() => onUnlink(row)}
                    className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded text-xs inline-flex items-center gap-1"
                    title="Снять связь BC.Pilot ↔ SD.Organization">
              <Link2Off size={11} /> Отвязать
            </button>
          )}
          {!row.sd_installation_id && (row.status === 'active' || row.status === 'planned') && (
            <button onClick={() => onMigrate(row)}
                    className="bg-blue-800 hover:bg-blue-700 text-blue-100 px-2 py-1 rounded text-xs inline-flex items-center gap-1"
                    title="Создать Installation в SD (можно выбрать существующую организацию)">
              <GitMerge size={11} /> Принять в работу
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function MigratePipelineModal({ row, onClose, onDone }: {
  row: PipelineRow; onClose: () => void; onDone: () => void;
}) {
  type Mode = 'new' | 'existing';
  const [mode, setMode] = useState<Mode>('new');
  const [targetOrgId, setTargetOrgId] = useState<string | null>(null);
  const [phase, setPhase] = useState<PilotPhase>('trial');
  const [deployment, setDeployment] = useState<'tenant' | 'onprem'>(
    (row.deployment_type === 'onprem' ? 'onprem' : 'tenant'));
  const [note, setNote] = useState('');
  const [inn, setInn] = useState(row.customer_inn || '');
  const [hint, setHint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // FNS search-by-name (v0.9.46)
  const [fnsQuery, setFnsQuery] = useState(row.customer_org || row.customer_name || '');
  const [fnsHits, setFnsHits] = useState<Array<{ name: string; inn: string; legal_address: string }>>([]);
  const [fnsSearching, setFnsSearching] = useState(false);

  const lookupFns = async () => {
    if (!inn.trim()) { setErr('Введите ИНН для поиска в ФНС'); return; }
    setErr(null); setHint(null);
    try {
      const { fnsApi } = await import('../api/v2');
      const r = await fnsApi.lookup(inn.trim());
      setHint(`ФНС: ${r.name}${r.legal_address ? ' • ' + r.legal_address : ''}`);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Не удалось найти ИНН в ФНС');
    }
  };

  const searchFnsByName = async () => {
    if (!fnsQuery.trim() || fnsQuery.trim().length < 3) {
      setErr('Минимум 3 символа для поиска по названию');
      return;
    }
    setFnsSearching(true); setErr(null); setFnsHits([]);
    try {
      const { fnsApi } = await import('../api/v2');
      const r = await fnsApi.search(fnsQuery.trim(), 10);
      setFnsHits(r);
      if (r.length === 0) setErr('В ЕГРЮЛ ничего не найдено по этому запросу');
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Поиск в ФНС не удался');
    } finally { setFnsSearching(false); }
  };

  const pickFnsHit = (h: { name: string; inn: string; legal_address: string }) => {
    setInn(h.inn);
    setHint(`ФНС: ${h.name}${h.legal_address ? ' • ' + h.legal_address : ''}`);
    setFnsHits([]);
  };

  const submit = async () => {
    if (mode === 'existing' && !targetOrgId) {
      setErr('Выберите организацию из списка');
      return;
    }
    if (mode === 'new' && !inn.trim()) {
      setErr('Укажите ИНН — без него карточку клиента создать нельзя.');
      return;
    }
    setBusy(true); setErr(null);
    try {
      const result = await pilotsApi.migrate({
        bc_pilot_id: row.bc_pilot_id,
        pilot_phase: phase,
        deployment_type: deployment,
        target_organization_id: mode === 'existing' ? targetOrgId! : undefined,
        inn_override: mode === 'new' ? (inn.trim() || undefined) : undefined,
        note: note || undefined,
      });
      // Защита от случайного объединения: если migrate нашёл existing org
      // с принципиально другим именем — спрашиваем подтверждение.
      if (result.name_mismatch_warning && mode === 'new') {
        const ok = window.confirm(
          result.name_mismatch_warning +
          '\n\nЕсли это всё-таки тот же клиент — нажмите OK. Если нет — отмените и нажмите «Отвязать» в строке воронки.'
        );
        if (!ok) {
          // Откатываем привязку через unlink, чтобы не оставлять ошибочно
          // связанный пилот.
          try { await pilotsApi.unlink(row.bc_pilot_id); } catch { /* ignore */ }
          setErr('Отменено. Связь снята. Введите правильный ИНН или выберите «Привязать к существующей».');
          return;
        }
      }
      onDone();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка миграции');
    } finally { setBusy(false); }
  };

  const canSubmit = !busy && (
    (mode === 'new' && inn.trim()) || (mode === 'existing' && targetOrgId)
  );

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-white">Принять пилот в работу</h3>
        <p className="text-xs text-slate-400 mt-1">
          BC.Pilot #{row.bc_pilot_id}: {row.customer_org || row.customer_name}
        </p>
        <p className="text-xs text-slate-500 mt-2">
          В SD появится Installation в режиме «Пилот» с датами старта/окончания
          и контактами из BC. Этот пилот переедет в таб «Развёрнутые».
        </p>

        {/* Выбор режима: новая org или существующая */}
        <div className="mt-4 flex gap-2 bg-slate-950 border border-slate-800 rounded p-1">
          <button onClick={() => setMode('new')}
                  className={`flex-1 px-3 py-1.5 rounded text-xs ${
                    mode === 'new' ? 'bg-blue-700 text-white'
                                   : 'text-slate-400 hover:text-slate-200'}`}>
            Создать новую организацию
          </button>
          <button onClick={() => setMode('existing')}
                  className={`flex-1 px-3 py-1.5 rounded text-xs ${
                    mode === 'existing' ? 'bg-blue-700 text-white'
                                        : 'text-slate-400 hover:text-slate-200'}`}>
            Привязать к существующей
          </button>
        </div>

        {mode === 'new' ? (
          <div className="mt-3 space-y-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">
                ИНН клиента {row.customer_inn ? '(из BC)' : '(в BC не заполнен)'}
              </label>
              <div className="flex gap-1">
                <input value={inn} onChange={e => setInn(e.target.value)} required
                       className="flex-1 bg-slate-950 text-slate-200 border border-slate-700 rounded px-2 py-1 text-sm font-mono"
                       placeholder="10/12 цифр" />
                <button type="button" onClick={lookupFns}
                        title="Проверить ИНН в ЕГРЮЛ"
                        className="bg-blue-700 hover:bg-blue-600 text-white px-3 rounded text-xs whitespace-nowrap">
                  ↺ ФНС по ИНН
                </button>
              </div>
              {hint && (
                <div className="mt-1 text-[11px] text-emerald-300">{hint}</div>
              )}
            </div>

            {/* Поиск по названию — для случая когда ИНН неизвестен (v0.9.46) */}
            <div className="bg-slate-950/50 border border-slate-800 rounded p-2">
              <label className="block text-xs text-slate-400 mb-1">
                Не знаете ИНН? Найти в ЕГРЮЛ по названию
              </label>
              <div className="flex gap-1">
                <input value={fnsQuery} onChange={e => setFnsQuery(e.target.value)}
                       onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchFnsByName(); } }}
                       className="flex-1 bg-slate-950 text-slate-200 border border-slate-700 rounded px-2 py-1 text-sm"
                       placeholder='Например: "Информационный центр"' />
                <button type="button" onClick={searchFnsByName} disabled={fnsSearching}
                        className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-slate-200 px-3 rounded text-xs whitespace-nowrap">
                  {fnsSearching ? '…' : '🔎 Найти'}
                </button>
              </div>
              {fnsHits.length > 0 && (
                <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                  {fnsHits.map(h => (
                    <button key={h.inn} type="button" onClick={() => pickFnsHit(h)}
                            className="block w-full text-left bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded p-2 text-xs">
                      <div className="text-slate-200">{h.name}</div>
                      <div className="text-slate-500 font-mono">ИНН: {h.inn}</div>
                      {h.legal_address && (
                        <div className="text-slate-600 truncate">{h.legal_address}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {!row.customer_inn && fnsHits.length === 0 && (
              <div className="text-[11px] text-amber-300">
                В BC ИНН пуст. Введите вручную или найдите в ЕГРЮЛ по
                названию выше. Если карточка в SD уже есть под другим
                именем (например ТЕДО ↔ Технологии Доверия) — переключитесь
                на «Привязать к существующей».
              </div>
            )}
          </div>
        ) : (
          <div className="mt-3">
            <label className="block text-xs text-slate-400 mb-1">
              Существующая организация SD
            </label>
            <OrgCombobox value={targetOrgId}
                         onChange={(id) => setTargetOrgId(id)}
                         placeholder="Поиск по названию или ИНН…" clearable />
            <div className="mt-1 text-[11px] text-slate-500">
              Installation создастся в выбранной организации. Её существующие
              реквизиты и контакты НЕ затрутся. ИНН берётся из её карточки.
            </div>
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Тип развёртывания</label>
            <div className="flex gap-1 bg-slate-950 border border-slate-700 rounded p-0.5">
              <button type="button" onClick={() => setDeployment('tenant')}
                      className={`flex-1 px-2 py-1 rounded text-xs ${
                        deployment === 'tenant' ? 'bg-blue-700 text-white'
                                                : 'text-slate-400 hover:text-slate-200'}`}>
                Tenant
              </button>
              <button type="button" onClick={() => setDeployment('onprem')}
                      className={`flex-1 px-2 py-1 rounded text-xs ${
                        deployment === 'onprem' ? 'bg-blue-700 text-white'
                                                : 'text-slate-400 hover:text-slate-200'}`}>
                On-prem
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Фаза пилота</label>
            <select value={phase} onChange={e => setPhase(e.target.value as PilotPhase)}
                    className="w-full bg-slate-950 text-slate-200 border border-slate-700 rounded px-2 py-1 text-sm">
              <option value="poc">PoC</option>
              <option value="trial">Trial</option>
              <option value="decision">Решение</option>
            </select>
          </div>
        </div>

        <div className="mt-3">
          <label className="block text-xs text-slate-400 mb-1">Заметка PM</label>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                    className="w-full bg-slate-950 text-slate-200 border border-slate-700 rounded px-2 py-1 text-sm"
                    placeholder="Цели пилота, контакты, договорённости…" />
        </div>

        {err && (
          <div className="mt-3 bg-rose-900/30 border border-rose-800 text-rose-200 px-3 py-2 rounded text-xs">
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} disabled={busy}
                  className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded text-sm">
            Отмена
          </button>
          <button onClick={submit} disabled={!canSubmit}
                  className="bg-blue-700 hover:bg-blue-600 disabled:bg-slate-700 text-white px-3 py-1.5 rounded text-sm">
            {busy ? 'Создаю…' : 'Принять в работу'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── PilotEditModal: редактирование развёрнутого пилота (v0.9.56) ────────────
function PilotEditModal({ row, onClose, onDone }: {
  row: PilotRow; onClose: () => void; onDone: () => void;
}) {
  const [started, setStarted] = useState<string>(row.started_at || '');
  const [plannedEnd, setPlannedEnd] = useState<string>(row.planned_end_at || '');
  const [phase, setPhase] = useState<PilotPhase | ''>(row.phase || '');
  const [deployment, setDeployment] = useState<'tenant' | 'onprem' | ''>('');
  const [notes, setNotes] = useState<string>(row.notes || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const { orgsApi } = await import('../api/organizations');
      const payload: Record<string, unknown> = {};
      payload.pilot_started_at = started || null;
      payload.pilot_planned_end_at = plannedEnd || null;
      payload.pilot_phase = phase || null;
      payload.pilot_notes = notes || null;
      if (deployment) payload.installation_type = deployment;
      await orgsApi.updateInstall(row.organization_id, row.installation_id, payload);
      onDone();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка сохранения');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-white">Редактировать пилот</h3>
        <p className="text-xs text-slate-400 mt-1">
          {row.organization_name} → {row.installation_name}
        </p>
        <p className="text-xs text-slate-500 mt-2">
          Меняются даты/фаза/заметки/тип развёртывания. Сам факт «это пилот»
          не убирается — для этого используйте «Конвертировать» или «Закрыть».
        </p>

        <div className="mt-4 border-t border-slate-800 pt-3">
          <div className="text-xs uppercase tracking-wider text-amber-400 font-bold mb-2">
            Даты пилота
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Дата старта *</label>
              <input type="date" value={started} onChange={e => setStarted(e.target.value)}
                     style={{ colorScheme: 'dark' }}
                     className="w-full bg-slate-950 text-slate-200 border border-slate-700 rounded px-2 py-1.5 text-sm" />
              {!started && (
                <div className="text-[10px] text-amber-300 mt-0.5">
                  не задана — пилот появится в «Без даты конца»
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">План окончания *</label>
              <input type="date" value={plannedEnd} onChange={e => setPlannedEnd(e.target.value)}
                     style={{ colorScheme: 'dark' }}
                     className="w-full bg-slate-950 text-slate-200 border border-slate-700 rounded px-2 py-1.5 text-sm" />
              {!plannedEnd && (
                <div className="text-[10px] text-amber-300 mt-0.5">
                  не задана — алерт-крон не сработает
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-3">
          <label className="block text-xs text-slate-400 mb-1">Фаза</label>
          <select value={phase} onChange={e => setPhase((e.target.value as PilotPhase) || '')}
                  className="w-full bg-slate-950 text-slate-200 border border-slate-700 rounded px-2 py-1 text-sm">
            <option value="">— не указана —</option>
            <option value="poc">PoC</option>
            <option value="trial">Trial</option>
            <option value="decision">Решение</option>
          </select>
        </div>

        <div className="mt-3">
          <label className="block text-xs text-slate-400 mb-1">Тип развёртывания</label>
          <div className="flex gap-1 bg-slate-950 border border-slate-700 rounded p-0.5">
            {(['tenant', 'onprem', ''] as const).map(v => (
              <button key={v} type="button" onClick={() => setDeployment(v)}
                      className={`flex-1 px-2 py-1 rounded text-xs ${
                        deployment === v ? 'bg-blue-700 text-white'
                                         : 'text-slate-400 hover:text-slate-200'}`}>
                {v === 'tenant' ? 'Tenant' : v === 'onprem' ? 'On-prem' : '— не менять —'}
              </button>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            Текущее значение не показывается — выберите «не менять» чтобы оставить как было.
          </div>
        </div>

        <div className="mt-3">
          <label className="block text-xs text-slate-400 mb-1">Заметки PM (история пилота)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={5}
                    className="w-full bg-slate-950 text-slate-200 border border-slate-700 rounded px-2 py-1 text-sm" />
        </div>

        {err && (
          <div className="mt-3 bg-rose-900/30 border border-rose-800 text-rose-200 px-3 py-2 rounded text-xs">
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} disabled={busy}
                  className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded text-sm">
            Отмена
          </button>
          <button onClick={submit} disabled={busy}
                  className="bg-blue-700 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-sm">
            {busy ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}
